import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../../bmr/services/api/axios.config";
import { notify } from "../../../bmr/utils/methods/notification";
import { TabPresetMap, tabPropertyKeys } from "../components/constant/constant";
import httpService from "../../../services/httpService";

const filterFieldProperty = (field) => {
  const fieldType = field.property?.field_type;
  if (!fieldType) return field;
  const activeTabs = TabPresetMap[fieldType] || [];
  const allowedKeys = new Set(["field_type", "field_layout"]);
  activeTabs.forEach((tab) => {
    const keys = tabPropertyKeys[tab] || [];
    keys.forEach((key) => allowedKeys.add(key));
  });
  const filteredProperty = {};
  Object.keys(field.property).forEach((key) => {
    if (allowedKeys.has(key)) {
      filteredProperty[key] = field.property[key];
    }
  });
  return { ...field, property: filteredProperty };
};

const initialState = {
  isLoading: false,
  section: [], // this state is used to store the last updated value fetched from GET API (Used in autosave to compare difference in changes of schema data)
  formData: { sections: [] },
  statusCode: null,
};
// Auto Save API
export const sectionAutoSave = createAsyncThunk(
  "bmr/sectionAutoSave",
  async (args, { rejectWithValue }) => {
    const { id, data } = args;
    try {
      const filteredData = {
        sections: data.sections.map((section) => ({
          ...section,
          fields: section.fields.map(filterFieldProperty),
        })),
      };
      const response = await api.post(
        `/ftb/templates/${id}/autosave`,
        filteredData,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message,
      );
    }
  },
);

// Fetch all the sections
export const fetchSections = createAsyncThunk(
  "bmr/fetchsectionAutoSave",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ftb/templates/${id}/sections`);
      return response.data;
    } catch (error) {
      notify.error(error || error?.message || error?.data?.message);
      return rejectWithValue(
        error.response ? error.response.data : error.message,
      );
    }
  },
);

export const saveAsDraft = createAsyncThunk(
  "bmr/saveAsDraft",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.put(`/ftb/templates/${id}/save-as-draft`);
      notify.success(response.data.message);
      return response.data;
    } catch (error) {
      notify.error(error || error?.message || error?.data?.message);
      return rejectWithValue(
        error.response ? error.response.data : error.message,
      );
    }
  },
);

export const submitSection = createAsyncThunk(
  "bmr/submit",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.put(`/ftb/templates/${id}/submit`);
      notify.success(response.data.message);
      return response.data;
    } catch (error) {
      notify.error(error || error?.message || error?.data?.message);
      return rejectWithValue(
        error.response ? error.response.data : error.message,
      );
    }
  },
);

// API to publish template
export const pusblishTemplate = createAsyncThunk(
  "bmr/pusblishTemplate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.put(`/ftb/templates/${id}/publish`);
      notify.success(response.data.message);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message,
      );
    }
  },
);

//API to delete section
export const deleteSection = createAsyncThunk(
  "bmr/deleteSection",
  async ({ id, sectionId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/ftb/templates/${id}/sections/${sectionId}/delete`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message,
      );
    }
  },
);

// API to delete fields from the sections
export const deleteField = createAsyncThunk(
  "bmr/deleteField",
  async ({ sectionId, fieldId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/ftb/templates/sections/${sectionId}/field/${fieldId}/delete`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message,
      );
    }
  },
);

// API to reorder sections
export const sectionReorder = createAsyncThunk(
  "bmr/sectionReorder",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/ftb/templates/${id}/sections/reorder`,
        data,
      );
      notify.success(response.data.message);
      return response.data;
    } catch (error) {
      notify.error(error || error?.message || error?.data?.message);
      return rejectWithValue(
        error.response ? error.response.data : error.message,
      );
    }
  },
);

// Creating thunk for the validator
// Thunk of the validator:

export const OnSubmitValidator = createAsyncThunk(
  "bmr/OnSubmitValidator",
  async (data, { rejectWithValue }) => {
    try {
      const response = await httpService.post(`/validateFormAnswer`, data);
      notify.success(response?.data?.message);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const bmrFbSectionsSlices = createSlice({
  name: "bmrFbSections",
  initialState,
  reducers: {
    // submit data is used to update the redux state section & formdata
    submitData(state, action) {
      state.section = action.payload;
      state.formData = action.payload;
    },
    // setFormData is used to update the formData state
    setFormData: (state, action) => {
      state.formData = JSON.parse(JSON.stringify(action.payload));
    },
    setStatusCode(state, action) {
      state.statusCode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      //@info created slice for the auto save
      .addCase(sectionAutoSave.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sectionAutoSave.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(sectionAutoSave.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchSections.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchSections.fulfilled, (state, action) => {
        state.isLoading = false;
        state.section = action.payload.data;
      })
      .addCase(fetchSections.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(saveAsDraft.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(saveAsDraft.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(saveAsDraft.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(submitSection.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(submitSection.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(submitSection.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteSection.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteSection.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteSection.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteField.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteField.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteField.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(pusblishTemplate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(pusblishTemplate.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(pusblishTemplate.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(sectionReorder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sectionReorder.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(sectionReorder.rejected, (state) => {
        state.isLoading = false;
      })

      //@info OnSubmitValidator slice
      .addCase(OnSubmitValidator.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(OnSubmitValidator.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(OnSubmitValidator.rejected, (state) => {
        state.isLoading = false;
      });
  },
});
export const { submitData } = bmrFbSectionsSlices.actions;
export const { setFormData } = bmrFbSectionsSlices.actions;
export const { setStatusCode } = bmrFbSectionsSlices.actions;

export default bmrFbSectionsSlices.reducer;
